/**
 * Tipos TS espejo de los Pydantic del local-api.
 *
 * En Fase 2, cuando arme `packages/sync-protocol/typescript`, generaré
 * estos tipos automáticamente desde el OpenAPI del local-api. Por ahora,
 * mantenerlos a mano evita dependencias de codegen.
 */

// ---------- Enums espejo del backend ---------------------------------------
export const ProductType = {
  GOODS: "goods",
  SERVICE: "service",
  BOTTLE: "bottle",
  DRINK_BY_SERVE: "drink_by_serve",
} as const;
export type ProductType = (typeof ProductType)[keyof typeof ProductType];

export const PaymentMethod = {
  CASH: "cash",
  CARD_CREDIT: "card_credit",
  CARD_DEBIT: "card_debit",
  YAPE: "yape",
  PLIN: "plin",
  TRANSFER: "transfer",
  OTHER: "other",
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const DocumentType = {
  BOLETA: "boleta",
  FACTURA: "factura",
  NOTA_VENTA: "nota_venta",
  NOTA_CREDITO: "nota_credito",
  NOTA_DEBITO: "nota_debito",
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const SaleStatus = {
  DRAFT: "draft",
  ISSUED: "issued",
  VOIDED: "voided",
} as const;
export type SaleStatus = (typeof SaleStatus)[keyof typeof SaleStatus];

export const CustomerDocType = {
  DNI: "dni",
  RUC: "ruc",
  FOREIGN: "foreign",
} as const;
export type CustomerDocType = (typeof CustomerDocType)[keyof typeof CustomerDocType];

// ---------- Auth -----------------------------------------------------------
export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface UserOut {
  id: string;
  username: string;
  full_name: string;
  role: string;
  branch_id: string | null;
  email?: string | null;
}

export interface CashierOut {
  id: string;
  full_name: string;
}

export const UserRole = {
  TENANT_OWNER: "tenant_owner",
  TENANT_ADMIN: "tenant_admin",
  BRANCH_ADMIN: "branch_admin",
  CASHIER: "cashier",
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** Versión usada por el CRUD admin. Distinto de UserOut (auth/me): incluye
 *  is_active + has_password/has_pin para que el form sepa si las credenciales
 *  ya están seteadas. Nunca exponemos los hashes. */
export interface AdminUserOut {
  id: string;
  username: string;
  full_name: string;
  role: UserRole;
  branch_id: string | null;
  email: string | null;
  is_active: boolean;
  has_password: boolean;
  has_pin: boolean;
}

// ---------- Catálogo -------------------------------------------------------
export interface BrandOut {
  id: string;
  name: string;
  is_active: boolean;
}

export interface UnitOut {
  id: string;
  code: string;
  name: string;
  allows_decimal: boolean;
}

export interface CategoryOut {
  id: string;
  name: string;
  parent_id: string | null;
  display_order: number;
  is_active: boolean;
}

export interface ProductOut {
  id: string;
  sku: string;
  barcode: string | null;
  name: string;
  description: string | null;
  product_type: ProductType;
  category_id: string | null;
  brand_id: string | null;
  unit_id: string | null;
  /** GROSS (con IGV) en la convención del POS. */
  base_price: string;
  igv_rate: string | null;
  igv_exempt: boolean;
  track_inventory: boolean;
  /** Umbral de alerta de stock (todas las sucursales). null = sin alerta. */
  min_stock: string | null;
  is_active: boolean;
  /** Path relativo bajo /media/. NULL = sin imagen. Construir URLs con
   *  productImageUrl()/productThumbUrl() de @/lib/media.ts. */
  image_path: string | null;
}

export interface CustomerOut {
  id: string;
  doc_type: CustomerDocType;
  doc_number: string;
  full_name: string;
  address: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
}

// ---------- Tenant ---------------------------------------------------------
export interface TenantOut {
  id: string;
  ruc: string;
  legal_name: string;
  trade_name: string | null;
  fiscal_address: string | null;
  plan: string;
  status: string;
  activated_at: string | null;
}

// ---------- Branches y series ----------------------------------------------
export interface BranchOut {
  id: string;
  code: string;
  name: string;
  address: string | null;
  phone: string | null;
  is_active: boolean;
}

export interface DocumentSeriesOut {
  id: string;
  branch_id: string;
  document_type: string;
  series_code: string;
  next_number: number;
  is_active: boolean;
}

// ---------- Terminals (cajas físicas) -------------------------------------
export interface TerminalOut {
  id: string;
  branch_id: string;
  code: string;
  name: string;
  default_boleta_series_id: string | null;
  default_factura_series_id: string | null;
  default_nota_venta_series_id: string | null;
  is_active: boolean;
  /** Ticketera de red (ESC/POS por TCP). Solo la usa el server local. */
  printer_host: string | null;
  printer_port: number;
  /** Caracteres por línea: 48 en 80 mm, 32 en 58 mm. */
  printer_columns: number;
  printer_open_drawer: boolean;
}

export interface TerminalCreateInput {
  code: string;
  name: string;
  default_boleta_series_id?: string | null;
  default_factura_series_id?: string | null;
  default_nota_venta_series_id?: string | null;
  is_active?: boolean;
  printer_host?: string | null;
  printer_port?: number;
  printer_columns?: number;
  printer_open_drawer?: boolean;
}

export interface TerminalUpdateInput {
  code?: string;
  name?: string;
  default_boleta_series_id?: string | null;
  default_factura_series_id?: string | null;
  default_nota_venta_series_id?: string | null;
  is_active?: boolean;
  printer_host?: string | null;
  printer_port?: number;
  printer_columns?: number;
  printer_open_drawer?: boolean;
}

export interface PrintResult {
  ok: boolean;
  printer: string;
}

// ---------- Cash sessions --------------------------------------------------
export interface CashSessionOut {
  id: string;
  branch_id: string;
  terminal_id: string;
  cashier_id: string;
  opened_at: string;
  opened_amount: string;
  closed_at: string | null;
  declared_amount: string | null;
  expected_amount: string | null;
  notes: string | null;
  /** Efectivo esperado en vivo. Para sesión abierta = computado on-demand;
   *  para cerrada = igual a expected_amount. */
  expected_cash: string;
}

// ---------- Sales ----------------------------------------------------------
export interface SaleItemOut {
  id: string;
  sale_id: string;
  product_id: string;
  line_number: number;
  product_name: string;
  product_sku: string;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  igv_rate: string;
  igv_amount: string;
  igv_exempt: boolean;
  line_total: string;
  /** Solo al agregar ítem: stock que quedaría al emitir. null = no controla inventario. */
  stock_available?: string | null;
}

export interface PaymentOut {
  id: string;
  sale_id: string;
  method: PaymentMethod;
  amount: string;
  received_at: string;
  reference: string | null;
  change_given: string;
}

export interface SaleOut {
  id: string;
  branch_id: string;
  terminal_id: string;
  cashier_id: string;
  cash_session_id: string | null;
  customer_id: string | null;
  series_id: string | null;
  series_code: string | null;
  document_type: string | null;
  document_number: number | null;
  status: SaleStatus;
  currency: string;
  subtotal: string;
  igv_amount: string;
  discount_total: string;
  total: string;
  issued_at: string | null;
  voided_at: string | null;
  voided_by_user_id: string | null;
  void_reason: string | null;
  notes: string | null;
}

export interface SaleDetailOut extends SaleOut {
  items: SaleItemOut[];
  payments: PaymentOut[];
  /** Cliente embebido si la venta tenía customer_id. null si fue venta sin cliente. */
  customer: CustomerOut | null;
  /** Estado SUNAT. null mientras el cloud no lo generó (o no es boleta/factura). */
  electronic_document?: ElectronicDocumentOut | null;
}

export type ElectronicDocumentStatus = "pending" | "accepted" | "observed" | "rejected";

export interface ElectronicDocumentOut {
  id: string;
  status: ElectronicDocumentStatus;
  provider: string;
  sunat_code: string | null;
  sunat_message: string | null;
  hash_code: string | null;
  qr_text: string | null;
  /** QR listo para <img src> (data URI SVG generado por el servidor). */
  qr_image: string | null;
  xml_url: string | null;
  cdr_url: string | null;
  pdf_url: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  void_status: "pending" | "accepted" | "rejected" | null;
  void_message: string | null;
}

// ---------- Inputs (request bodies) ---------------------------------------
export interface AddSaleItemRequest {
  product_id: string;
  quantity: string;
  unit_price?: string | null;
  discount_amount?: string;
}

export interface PaymentInput {
  method: PaymentMethod;
  amount: string;
  reference?: string | null;
  change_given?: string;
}

export interface IssueSaleRequest {
  /** Si se omite, el backend resuelve por la serie default del terminal según
   *  document_type. Al menos uno de los dos debe enviarse. */
  series_id?: string | null;
  document_type?: DocumentType | null;
  customer_id?: string | null;
  payments: PaymentInput[];
}

// ---------- Error envelope -------------------------------------------------
export interface ApiErrorBody {
  error_code: string;
  message: string;
  details?: Record<string, unknown>;
}

// ---------- Inventario --------------------------------------------------------

export type ManualMovementType = "initial" | "purchase" | "adjustment" | "loss";

export interface StockOut {
  product_id: string;
  sku: string;
  barcode: string | null;
  name: string;
  unit_id: string | null;
  stock: string;
  min_stock: string | null;
  /** Stock negativo o en/bajo el mínimo. */
  is_low: boolean;
  /** Costo promedio de compras (null si nunca se cargó un costo). */
  average_cost: string | null;
  /** Valorización: stock positivo × costo promedio. */
  stock_value: string | null;
}

export type TransferStatus = "sent" | "received" | "cancelled";

export interface TransferOut {
  id: string;
  from_branch_id: string;
  to_branch_id: string;
  status: TransferStatus;
  lines: { product_id: string; quantity: string; received_quantity: string | null }[];
  notes: string | null;
  sent_at: string;
  received_at: string | null;
}

export interface InventoryMovementOut {
  id: string;
  branch_id: string;
  product_id: string;
  /** initial, purchase, sale, adjustment, loss, transfer_in, transfer_out, return_in */
  movement_type: string;
  quantity_delta: string;
  unit_cost: string | null;
  reference_type: string | null;
  reference_id: string | null;
  performed_by_user_id: string | null;
  notes: string | null;
  occurred_at: string;
}

export interface KardexEntryOut extends InventoryMovementOut {
  balance_after: string;
}

export interface KardexOut {
  product_id: string;
  branch_id: string;
  stock: string;
  movements: KardexEntryOut[];
}

export interface CountResultOut {
  product_id: string;
  previous: string;
  counted: string;
  delta: string;
}

// ---------- Facturación electrónica y sync -------------------------------------

/** "unsent": la venta todavía no tiene comprobante (no subió al cloud o el worker no la tomó). */
export type BillingStatus = "unsent" | ElectronicDocumentStatus;

export interface BillingRowOut {
  sale_id: string;
  branch_id: string;
  document_type: string;
  series_code: string;
  document_number: number;
  sale_status: string;
  total: string;
  issued_at: string | null;
  document_id: string | null;
  status: BillingStatus;
  attempts: number;
  last_error: string | null;
  sunat_code: string | null;
  sunat_message: string | null;
  void_status: "pending" | "accepted" | "rejected" | null;
  void_message: string | null;
  accepted_at: string | null;
}

export interface SyncStatusOut {
  enabled: boolean;
  node_id: string;
  cloud_base_url: string;
  pending_events: number;
  last_pulled_cursor: number;
  last_push_at: string | null;
  last_pull_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  conflicts: number;
}

export interface SyncConflictOut {
  id: number;
  /** push: el cloud lo rechazó · pull: no se pudo aplicar en este server */
  direction: "push" | "pull";
  event_id: string;
  aggregate_type: string;
  aggregate_id: string;
  status: string;
  reason: string | null;
  created_at: string;
}

// ---------- Reportes ------------------------------------------------------------

export interface ReportBucket {
  key: string;
  label: string;
  count: number;
  total: string;
}

export interface SalesSummaryOut {
  sales_count: number;
  total: string;
  igv: string;
  discounts: string;
  average_ticket: string;
  voided_count: number;
  voided_total: string;
  by_day: ReportBucket[];
  by_branch: ReportBucket[];
  by_cashier: ReportBucket[];
  by_payment_method: ReportBucket[];
  by_document_type: ReportBucket[];
}

export interface ProductSalesOut {
  product_id: string;
  sku: string;
  name: string;
  quantity: string;
  total: string;
  net_total: string;
  /** null si el producto no tiene compras/carga inicial con costo. */
  average_cost: string | null;
  estimated_cost: string | null;
  margin: string | null;
}

export interface ProductStockOut {
  product_id: string;
  sku: string;
  name: string;
  total: string;
  min_stock: string | null;
  by_branch: { branch_id: string; quantity: string }[];
}

// ---------- Servidores locales (vista del cloud) ----------------------------------

export interface NodeStatusOut {
  id: string;
  name: string;
  branch_id: string | null;
  is_active: boolean;
  last_seen_at: string | null;
  last_push_at: string | null;
  last_pull_at: string | null;
  last_pull_cursor: number;
  /** Eventos del tenant que el nodo todavía no leyó (0 = al día). */
  lag_events: number;
  last_backup_name: string | null;
  last_backup_at: string | null;
}

export interface CloudConflictOut {
  id: number;
  node_id: string;
  event_id: string;
  aggregate_type: string;
  aggregate_id: string;
  status: string;
  reason: string | null;
  created_at: string;
}
